import { c as _c } from "react/compiler-runtime";
import { Timer } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";

// Таймер обратного отсчёта акции (П3, fd-promo-10): «До конца 03:12:45».
// Один интервал на таймер, раз в секунду; по нулю зовёт `onExpire` ровно один
// раз — страница товара в этот момент возвращает обычную цену.

/** Сколько миллисекунд осталось до момента; не меньше нуля. */
export const remainingMs = (endsAt: string, now = Date.now()): number => Math.max(0, Date.parse(endsAt) - now);

/** «чч:мм:сс», а дальше суток — «N д чч:мм». */
export const formatRemaining = (ms: number): string => {
  const total = Math.floor(ms / 1000);
  const days = Math.floor(total / 86_400);
  const h = Math.floor(total % 86_400 / 3600);
  const m = Math.floor(total % 3600 / 60);
  const s = total % 60;
  const pad = (n: number) => String(n).padStart(2, "0");
  return days > 0 ? `${days} д ${pad(h)}:${pad(m)}` : `${pad(h)}:${pad(m)}:${pad(s)}`;
};
export function useCountdown(endsAt: string | null | undefined, onExpire?: () => void): number | null {
  const [left, setLeft] = useState<number | null>(() => endsAt ? remainingMs(endsAt) : null);
  const expireRef = useRef(onExpire);
  expireRef.current = onExpire;
  useEffect(() => {
    if (!endsAt) {
      setLeft(null);
      return;
    }
    let fired = false;
    const tick = () => {
      const next = remainingMs(endsAt);
      setLeft(next);
      if (next <= 0 && !fired) {
        fired = true;
        expireRef.current?.();
      }
      return next;
    };
    if (tick() <= 0) return;
    const id = window.setInterval(() => {
      if (tick() <= 0) window.clearInterval(id);
    }, 1000);
    return () => window.clearInterval(id);
  }, [endsAt]);
  return left;
}
export function PromoCountdown(t0) {
  const $ = _c(13);
  const {
    endsAt,
    onExpire,
    label: t1,
    tone: t2,
    className
  } = t0;
  const label = t1 === undefined ? "\u0414\u043E \u043A\u043E\u043D\u0446\u0430" : t1;
  const tone = t2 === undefined ? "accent" : t2;
  const left = useCountdown(endsAt, onExpire);
  if (left == null || left <= 0) {
    return null;
  }
  const t3 = `${label || "\u0414\u043E \u043A\u043E\u043D\u0446\u0430 \u0430\u043A\u0446\u0438\u0438"} ${formatRemaining(left)}`;
  const t4 = tone === "accent" ? "h-6 bg-osmix-tangelo px-2 text-[12px] leading-4 text-white" : "h-7 bg-osmix-tangelo/10 px-2.5 text-[13px] leading-4 text-osmix-tangelo";
  let t5;
  if ($[0] !== className || $[1] !== t4) {
    t5 = cn("inline-flex items-center gap-1 whitespace-nowrap rounded-full font-semibold tabular-nums", t4, className);
    $[0] = className;
    $[1] = t4;
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  let t6;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Timer className="size-3.5 shrink-0" aria-hidden={true} />;
    $[3] = t6;
  } else {
    t6 = $[3];
  }
  const t7 = label ? `${label} ` : "";
  let t8;
  if ($[4] !== left) {
    t8 = formatRemaining(left);
    $[4] = left;
    $[5] = t8;
  } else {
    t8 = $[5];
  }
  let t9;
  if ($[6] !== t7 || $[7] !== t8) {
    t9 = <span>{t7}{t8}</span>;
    $[6] = t7;
    $[7] = t8;
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  let t10;
  if ($[9] !== t3 || $[10] !== t5 || $[11] !== t9) {
    t10 = <span data-testid="promo-countdown" role="timer" aria-label={t3} className={t5}>{t6}{t9}</span>;
    $[9] = t3;
    $[10] = t5;
    $[11] = t9;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  return t10;
}